import { sanitize } from '@ben/common/dom';
import { Radio } from 'ariakit/radio';
import type { ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import { variant } from 'styled-system';
import { slugify } from '../../utils/slugify';
import type { ITheme } from '../../utils/theme';
import { TypographyVariant } from '../../utils/theme';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import { Tooltip } from '../tooltip/tooltip';
import Typography from '../typography/typography';

export interface IRadioButtonProps extends Partial<HTMLInputElement> {
  label: ReactNode;
  variant?: RadioButtonVariant;
  typographyVariant?: TypographyVariant;
  showLabel?: boolean;
  backgroundColor?: string;
  className?: string;
  size?: number; // using this name as to not interfere with other props
  hintText?: string;
  disabled?: boolean;
  hasError?: boolean;
  description?: string;
}

export enum RadioButtonVariant {
  Default = 'default',
  Checked = 'checked',
  Locked = 'locked',
}
export const RadioButton = forwardRef(
  (
    {
      label,
      variant = RadioButtonVariant.Default,
      typographyVariant = TypographyVariant.bodyLg1,
      showLabel = true,
      size,
      className,
      hintText,
      disabled,
      backgroundColor,
      hasError = false,
      description,
      ...rest
    }: IRadioButtonProps & {
      onChange?: () => void;
    },
    ref
  ) => {
    const id = useId();
    const tooltipId = useId();

    const theme = useTheme();

    const labelIsString = typeof label === 'string';
    const testId = labelIsString
      ? `${slugify(label)}-radio-button`
      : 'radio-button';
    const inputValue = rest.value || (labelIsString ? label : undefined);

    return (
      <Container htmlFor={id} className={className}>
        <RadioInput
          ref={ref}
          data-testid={testId}
          {...rest}
          disabled={disabled}
          value={inputValue}
          id={id}
          variant={variant}
          size={size}
          $backgroundColor={backgroundColor}
          $hasError={hasError}
          aria-describedby={hintText ? tooltipId : undefined}
        />
        {showLabel && (
          <LabelWrapper>
            <Typography
              variant={typographyVariant}
              color={
                disabled
                  ? theme.palette.neutral[70]
                  : theme.palette.neutral[100]
              }
            >
              {labelIsString ? (
                <span dangerouslySetInnerHTML={{ __html: sanitize(label) }} />
              ) : (
                label
              )}
            </Typography>
            {description && (
              <Typography
                variant={TypographyVariant.bodySm1}
                color={theme.palette.neutral[90]}
              >
                {description}
              </Typography>
            )}
          </LabelWrapper>
        )}
        {hintText && (
          <Tooltip tooltipText={hintText} id={tooltipId}>
            <Icon
              name={IconName.Info}
              color={theme.palette.neutral[80]}
              scale={IconScale.S}
            />
          </Tooltip>
        )}
      </Container>
    );
  }
);

RadioButton.displayName = 'RadioButton';

export default RadioButton;

const Container = styled('label')`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: ${theme.spacing(1)};
  `}
`;

const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const baseStyle = ({
  $backgroundColor,
  $hasError,
  size,
  theme,
}: {
  $backgroundColor?: string;
  $hasError?: boolean;
  size?: number;
  theme: ITheme;
}) => css`
  width: ${typeof size === 'number' ? `${size}px` : '22px'};
  height: ${typeof size === 'number' ? `${size}px` : '22px'};
  border-radius: 50%;
  outline: none;
  box-shadow: 0 0 0 1px
    ${({ theme }) =>
      $hasError ? theme.palette.error.main : theme.palette.neutral[80]};
  -webkit-appearance: none;
  ${$backgroundColor ? `background: ${$backgroundColor};` : ''}
  cursor: pointer;
  &:hover:not(:disabled):not(:checked) {
    &::after {
      content: '';
      display: block;
      border-radius: 50%;
      width: ${typeof size === 'number' ? `${size - 4}px` : '18px'};
      height: ${typeof size === 'number' ? `${size - 4}px` : '18px'};
      margin: 2px auto;
      background: ${({ theme }) => theme.palette.neutral[50]};
      background-repeat: no-repeat;
      background-position: center;
    }
  }
  &:focus-visible {
    outline: ${({ theme }) => theme.palette.focus} solid 2px;
    outline-offset: 2px !important;
  }
  &::after {
    content: '';
    display: block;
    border-radius: 50%;
    margin: 2px auto;
  }
  &:checked {
    box-shadow: 0 0 0 1px
      ${({ theme }) =>
        $hasError ? theme.palette.error.main : theme.palette.primary.main};
    &::after {
      content: '';
      display: block;
      border-radius: 50%;
      width: ${typeof size === 'number' ? `${size - 4}px` : '18px'};
      height: ${typeof size === 'number' ? `${size - 4}px` : '18px'};
      margin: 2px auto;
      background: ${({ theme }) => theme.palette.primary.main};
      background-repeat: no-repeat;
      background-position: center;
    }
  }
  &:checked:disabled {
    &::after {
      opacity: 0.4;
    }
  }
  &:disabled {
    cursor: not-allowed;
    pointer-events: all !important;
    ${({ theme }) => css`
      background: ${theme.palette.neutral[40]};
      box-shadow: 0 0 0 1px ${theme.palette.neutral[60]};
    `}
  }
`;

const variantStyle = ({ theme }: { theme: DefaultTheme }) =>
  variant({
    variants: {
      default: {
        '&:checked': {
          '&::after': {
            background: theme.palette.primary.main,
          },
        },
      },
      checked: {
        '&:checked': {
          '&::after': {
            backgroundImage: css`url('data:image/svg+xml;utf8,<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 3.5L4 6.5L9.5 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ')`,
          },
        },
      },
      locked: {
        '&:checked': {
          '&::after': {
            backgroundImage: css`url('data:image/svg+xml;utf8,<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.7 4.80435V2.41304C8.7 1.3565 7.89411 0.5 6.9 0.5H5.1C4.10589 0.5 3.3 1.3565 3.3 2.41304V4.80435M1.5 6.23913C1.5 5.44672 2.10442 4.80435 2.85 4.80435H9.15C9.89558 4.80435 10.5 5.44672 10.5 6.23913V10.0652C10.5 10.8576 9.89558 11.5 9.15 11.5H2.85C2.10442 11.5 1.5 10.8576 1.5 10.0652V6.23913Z" stroke="white"/>
            <path d="M6 7.5V9" stroke="white" stroke-linecap="round"/>
            </svg>
            ')`,
          },
        },
      },
    },
  });

const RadioInput: StyledComponent<
  typeof Radio,
  DefaultTheme,
  Record<string, unknown> & { $backgroundColor?: string },
  never
> = styled(Radio)(baseStyle, variantStyle);
